// src/pages/portal/Orders.js
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";

export default function Orders() {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    getDocs(query(collection(db, "orders"), where("uid", "==", currentUser.uid), orderBy("createdAt", "desc")))
      .then(snap => { setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); })
      .catch(() => setLoading(false));
  }, [currentUser]);

  const statusStyle = {
    active: { cls: "badge-accent", label: "Active" },
    pending: { cls: "badge-amber", label: "Pending" },
    completed: { cls: "badge-blue", label: "Completed" },
    cancelled: { cls: "badge-red", label: "Cancelled" },
    expired: { cls: "badge-red", label: "Expired" },
  };

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: "1.5rem", fontWeight: 700, fontFamily: "var(--font-head)", marginBottom: 4 }}>My Orders</h1>
        <p style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>All your packages and service subscriptions.</p>
      </motion.div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 48 }}><div className="spinner" style={{ margin: "0 auto" }} /></div>
      ) : orders.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 24px", background: "var(--surface)", borderRadius: 16, border: "1px dashed var(--border-med)" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>📦</div>
          <h3 style={{ fontWeight: 600, marginBottom: 8 }}>No orders yet</h3>
          <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginBottom: 20 }}>Browse our packages and start your first project with us.</p>
          <Link to="/packages" className="btn btn-primary">View Packages</Link>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {orders.map((o, i) => {
            const s = statusStyle[o.status] || statusStyle.active;
            const expiryDate = o.expiresAt?.toDate?.();
            const daysLeft = expiryDate ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
            return (
              <motion.div key={o.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: "20px 24px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "1rem", fontFamily: "var(--font-head)", marginBottom: 4 }}>{o.packageName || "Service Package"}</div>
                      <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", display: "flex", gap: 16, flexWrap: "wrap" }}>
                        <span>Ordered: {o.createdAt?.toDate?.()?.toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "numeric" }) || "—"}</span>
                        {expiryDate && <span>Expires: {expiryDate.toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "numeric" })}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      {o.amount && <div style={{ fontSize: "1.125rem", fontWeight: 700 }}>₱{o.amount.toLocaleString()}</div>}
                      <span className={`badge ${s.cls}`}>{s.label}</span>
                    </div>
                  </div>

                  {/* Renewal warning */}
                  {daysLeft !== null && daysLeft <= 60 && daysLeft > 0 && (
                    <div style={{ marginTop: 14, padding: "10px 14px", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      <span style={{ fontSize: "0.875rem", color: "var(--accent-warm)" }}>⚠️ Subscription expires in <strong>{daysLeft} days</strong></span>
                      <Link to="/portal/billing" className="btn btn-sm" style={{ background: "var(--accent-warm)", color: "var(--navy)", fontWeight: 600 }}>Renew Now</Link>
                    </div>
                  )}

                  {o.notes && <div style={{ marginTop: 12, fontSize: "0.8125rem", color: "var(--text-muted)" }}>{o.notes}</div>}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
